/**
 * Custom hook for drag & drop scheduling functionality
 */
import { useState, useCallback, useRef } from 'react';
import { validateBookingMove, createDragLog, canDragBooking } from '../utils/dragDropSchedule';

export const useDragDropSchedule = (props) => {
  const { data, branchID, auth, month } = props;

  // Drag state management
  const [dragState, setDragState] = useState({
    isDragging: false,
    sourceRoom: null,
    sourceCellIndex: null,
    sourceBooking: null,
    sourceDate: null,
    currentDropZone: null,
    validationStatus: null, // 'valid', 'invalid', null
    validationMessage: '',
  });

  const [dragPreview, setDragPreview] = useState({
    show: false,
    x: 0,
    y: 0,
    content: ''
  });

  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    sourceRoom: null,
    targetRoom: null,
    sourceDate: null,
    targetDate: null,
    booking: null,
    validationMessage: '',
    isLoading: false
  });

  const dragLogRef = useRef([]);
  const lastOverRef = useRef(null); // key of last hovered cell

  /**
   * Start drag operation
   */
  const startDrag = useCallback((e, roomNumber, cellIndex, booking) => {
    // Check if user can drag
    if (!canDragBooking(branchID, auth.user.role, month, booking)) {
      console.log('❌ Cannot drag: Permission check failed');
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    lastOverRef.current = null;

    const sourceDate = `${month}-${String(cellIndex + 1).padStart(2, '0')}`;

    console.log('✅ Drag started:', { booking: booking.name, room: roomNumber, date: sourceDate });

    setDragState({
      isDragging: true,
      sourceRoom: roomNumber,
      sourceCellIndex: cellIndex,
      sourceBooking: booking,
      sourceDate: sourceDate,
      currentDropZone: null,
      validationStatus: null,
      validationMessage: '',
    });

    // Show drag preview
    const previewText = `${booking.name || 'Guest'} - Room ${roomNumber}`;
    setDragPreview({
      show: true,
      x: e.pageX,
      y: e.pageY,
      content: previewText
    });

    // Add source cell visual feedback
    const sourceCell = document.getElementById(`cell-${roomNumber}-${cellIndex}`);
    if (sourceCell) {
      sourceCell.classList.add('cell-being-dragged');
    }
  }, [branchID, auth, month]);

  /**
   * Handle drag over cell
   */
  const handleDragOver = useCallback((e, targetRoom, targetCellIndex) => {
    if (!dragState.isDragging) return;

    e.preventDefault();
    e.stopPropagation();

    // Skip all work while still hovering the same cell (dragover fires continuously)
    const overKey = `${targetRoom}-${targetCellIndex}`;
    if (lastOverRef.current === overKey) return;
    lastOverRef.current = overKey;

    // Validate drop zone
    if (dragState.sourceRoom === targetRoom && dragState.sourceCellIndex === targetCellIndex) {
      setDragState(prev => ({
        ...prev,
        currentDropZone: null,
        validationStatus: null
      }));
      return;
    }

    const targetDate = `${month}-${String(targetCellIndex + 1).padStart(2, '0')}`;
    const validation = validateBookingMove(
      dragState.sourceBooking,
      targetRoom,
      targetDate,
      data,
      month
    );

    setDragState(prev => ({
      ...prev,
      currentDropZone: `${targetRoom}-${targetCellIndex}`,
      validationStatus: validation.isValid ? 'valid' : 'invalid',
      validationMessage: validation.message
    }));

    // Update target cell visual feedback
    const targetCell = document.getElementById(`cell-${targetRoom}-${targetCellIndex}`);
    if (targetCell) {
      targetCell.classList.remove('drop-zone-valid', 'drop-zone-invalid', 'drop-zone-processing');
      if (validation.isValid) {
        targetCell.classList.add('drop-zone-valid');
      } else {
        targetCell.classList.add('drop-zone-invalid');
      }
    }
  }, [dragState, data, month]);

  /**
   * Handle drop
   */
  const handleDrop = useCallback((e, targetRoom, targetCellIndex) => {
    if (!dragState.isDragging) return;
    lastOverRef.current = null;

    e.preventDefault();
    e.stopPropagation();

    // Clean up visual feedback
    const sourceCell = document.getElementById(`cell-${dragState.sourceRoom}-${dragState.sourceCellIndex}`);
    if (sourceCell) {
      sourceCell.classList.remove('cell-being-dragged');
    }

    const targetCell = document.getElementById(`cell-${targetRoom}-${targetCellIndex}`);
    if (targetCell) {
      targetCell.classList.remove('drop-zone-valid', 'drop-zone-invalid', 'drop-zone-processing');
    }

    // Hide drag preview
    setDragPreview({ show: false, x: 0, y: 0, content: '' });

    // If same cell, cancel
    if (dragState.sourceRoom === targetRoom && dragState.sourceCellIndex === targetCellIndex) {
      setDragState({
        isDragging: false,
        sourceRoom: null,
        sourceCellIndex: null,
        sourceBooking: null,
        sourceDate: null,
        currentDropZone: null,
        validationStatus: null,
        validationMessage: '',
      });
      return;
    }

    // If invalid drop, cancel
    if (dragState.validationStatus !== 'valid') {
      setDragState({
        isDragging: false,
        sourceRoom: null,
        sourceCellIndex: null,
        sourceBooking: null,
        sourceDate: null,
        currentDropZone: null,
        validationStatus: null,
        validationMessage: '',
      });
      return;
    }

    // Show confirmation dialog
    const targetDate = `${month}-${String(targetCellIndex + 1).padStart(2, '0')}`;
    setConfirmDialog({
      show: true,
      sourceRoom: dragState.sourceRoom,
      targetRoom: targetRoom,
      sourceDate: dragState.sourceDate,
      targetDate: targetDate,
      booking: dragState.sourceBooking,
      validationMessage: dragState.validationMessage,
      isLoading: false
    });

    setDragState(prev => ({
      ...prev,
      isDragging: false
    }));
  }, [dragState, month]);

  /**
   * Cancel drag
   */
  const cancelDrag = useCallback(() => {
    lastOverRef.current = null;
    // Clean up visual feedback
    const sourceCell = document.getElementById(`cell-${dragState.sourceRoom}-${dragState.sourceCellIndex}`);
    if (sourceCell) {
      sourceCell.classList.remove('cell-being-dragged');
    }

    // Hide drag preview and dialog
    setDragPreview({ show: false, x: 0, y: 0, content: '' });
    setConfirmDialog(prev => ({ ...prev, show: false }));

    // Reset drag state
    setDragState({
      isDragging: false,
      sourceRoom: null,
      sourceCellIndex: null,
      sourceBooking: null,
      sourceDate: null,
      currentDropZone: null,
      validationStatus: null,
      validationMessage: '',
    });
  }, [dragState]);

  /**
   * Confirm drag and drop - API call to backend
   */
  const confirmDragDrop = useCallback(async (onConfirm) => {
    setConfirmDialog(prev => ({ ...prev, isLoading: true }));

    try {
      // Create log entry
      const resolvedBookingId = confirmDialog.booking?.id || confirmDialog.booking?._id || confirmDialog.booking?.booking_id;

      const logEntry = createDragLog({
        bookingId: resolvedBookingId,
        guestName: confirmDialog.booking.name,
        sourceRoom: confirmDialog.sourceRoom,
        targetRoom: confirmDialog.targetRoom,
        sourceDate: confirmDialog.sourceDate,
        targetDate: confirmDialog.targetDate,
        username: auth.user.username || auth.user.email,
        branchID: branchID,
        status: 'pending'
      });

      dragLogRef.current.push(logEntry);

      // Call backend API to update booking
      if (onConfirm) {
        const result = await onConfirm({
          bookingId: resolvedBookingId,
          sourceRoom: confirmDialog.sourceRoom,
          targetRoom: confirmDialog.targetRoom,
          sourceDate: confirmDialog.sourceDate,
          targetDate: confirmDialog.targetDate,
          booking: confirmDialog.booking,
          logs: dragLogRef.current
        });

        if (result.success) {
          logEntry.status = 'success';
          setConfirmDialog(prev => ({ ...prev, isLoading: false, show: false }));
          setDragState({
            isDragging: false,
            sourceRoom: null,
            sourceCellIndex: null,
            sourceBooking: null,
            sourceDate: null,
            currentDropZone: null,
            validationStatus: null,
            validationMessage: '',
          });
          return true;
        } else {
          logEntry.status = 'failed';
          setConfirmDialog(prev => ({
            ...prev,
            isLoading: false,
            validationMessage: result.message || 'Failed to update booking'
          }));
          return false;
        }
      }
    } catch (error) {
      console.error('Drag & drop error:', error);
      setConfirmDialog(prev => ({
        ...prev,
        isLoading: false,
        validationMessage: 'Error occurred while processing drag & drop'
      }));
      return false;
    }
  }, [confirmDialog, auth, branchID]);

  return {
    dragState,
    dragPreview,
    confirmDialog,
    dragLogRef,
    startDrag,
    handleDragOver,
    handleDrop,
    cancelDrag,
    confirmDragDrop,
    setDragPreview,
    setConfirmDialog
  };
};
